import React, {useState} from "react";
import {
	Attachment,
	DedicationImportResultDto,
	DedicationImportResultDtoStatusEnum,
	DedicationRestControllerApiFactory,
	ImportIssueDto
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../../components/utils/api";
import {Identifiable, useForm, UseFormProps} from "../../../../../components/form/form_model";
import {FormContent} from "../../../../../components/form/form_inputs/form_buttons";
import FormFile from "../../../../../components/form/form_inputs/form_file";
import {FormError, manageErrors, removePreviousErrors} from "../../../../../components/form/form_errors";
import {dateFromToString} from "../../../../../components/utils/dates";
import {fmtAmt} from "../../../../../components/utils/numbers";
import {
	Alert,
	AlertTitle,
	Box,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import {CloudUpload} from "@mui/icons-material";
import dayjs from "dayjs";

type Severity = "success" | "warning" | "error";

const SEVERITIES: Record<DedicationImportResultDtoStatusEnum, Severity> = {
	OK: "success",
	WARNING: "warning",
	ERROR: "error"
};

const TITLES: Record<DedicationImportResultDtoStatusEnum, string> = {
	OK: "Importación correcta",
	WARNING: "Importación terminada, revisa los avisos",
	ERROR: "Importación terminada con líneas sin importar"
};

const severityOf = (res: DedicationImportResultDto): Severity =>
	SEVERITIES[res.status || DedicationImportResultDtoStatusEnum.WARNING] || "warning";

const hours = (num: number | undefined): string => fmtAmt(num || 0) + " h";

/** Una frase que resume el resultado, para el snackbar y para la cabecera del panel. */
const summaryOf = (res: DedicationImportResultDto): string => {
	const parts = [`${res.entriesCreated || 0} dedicaciones creadas (${hours(res.hoursCreated)})`];
	if (res.entriesUpdated) {
		parts.push(`${res.entriesUpdated} actualizadas (${hours(res.hoursUpdated)})`);
	}
	if (res.entriesExisting) {
		parts.push(`${res.entriesExisting} ya existían`);
	}
	if (res.entriesFailed) {
		parts.push(`${res.entriesFailed} sin importar`);
	}
	return parts.join(", ") + ".";
};

/** El rango de días afectado, cerrado por los dos lados para leerlo sin traducir el "exclusive". */
const rangeOf = (res: DedicationImportResultDto): string => {
	if (!res.fromDateInclusive || !res.toDateExclusive) {
		return "—";
	}
	const last = dayjs(res.toDateExclusive).subtract(1, "day");
	return dateFromToString(res.fromDateInclusive) + " – " + last.format("DD/MM/YYYY");
};

const StatRow = ({label, value}: { label: string, value: React.ReactNode }) => (
	<TableRow>
		<TableCell sx={{width: "40%", color: "text.secondary", border: 0, py: 0.5}}>{label}</TableCell>
		<TableCell sx={{border: 0, py: 0.5}}>{value}</TableCell>
	</TableRow>
);

const ImportResult = ({res}: { res: DedicationImportResultDto }) => {
	const errors = res.errors || [];
	const warnings = res.warnings || [];

	return (
		<Box mt={3}>
			<Alert severity={severityOf(res)}>
				<AlertTitle>{TITLES[res.status || DedicationImportResultDtoStatusEnum.WARNING]}</AlertTitle>
				{summaryOf(res)}
			</Alert>

			<Table size="small" sx={{mt: 2, maxWidth: 620}}>
				<TableBody>
					<StatRow label="Líneas leídas del fichero" value={res.linesRead || 0}/>
					{!!res.linesRejected && (
						<StatRow label="Descartadas al leer el fichero" value={res.linesRejected}/>
					)}
					{!!res.linesMerged && (
						<StatRow label="Fusionadas por ser indistinguibles" value={res.linesMerged}/>
					)}
					<StatRow label="Horas en el fichero" value={hours(res.hoursInCsv)}/>
					<StatRow label="Dedicaciones creadas en Redmine"
							 value={`${res.entriesCreated || 0} (${hours(res.hoursCreated)})`}/>
					{!!res.entriesUpdated && (
						<StatRow label="Actualizadas porque el fichero traía otras horas"
								 value={`${res.entriesUpdated} (${hours(res.hoursUpdated)})`}/>
					)}
					<StatRow label="Ya estaban en Redmine"
							 value={`${res.entriesExisting || 0} (${hours(res.hoursExisting)})`}/>
					<StatRow label="Sin importar" value={res.entriesFailed || 0}/>
					<StatRow label="Días afectados" value={rangeOf(res)}/>
				</TableBody>
			</Table>

			{warnings.length > 0 && (
				<Alert severity="warning" sx={{mt: 2}}>
					<AlertTitle>Avisos</AlertTitle>
					<ul style={{margin: 0, paddingLeft: "1.2em"}}>
						{warnings.map((w, i) => <li key={"warn-" + i}>{w}</li>)}
					</ul>
				</Alert>
			)}

			{errors.length > 0 && (
				<Box mt={2}>
					<Typography variant="subtitle2" gutterBottom>
						Líneas que no han entrado
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Fila</TableCell>
								<TableCell>Proyecto</TableCell>
								<TableCell>Tarea</TableCell>
								<TableCell>Usuario</TableCell>
								<TableCell>Fecha</TableCell>
								<TableCell align="right">Horas</TableCell>
								<TableCell>Motivo</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{errors.map((err: ImportIssueDto, i: number) => (
								<TableRow key={"err-" + i}>
									<TableCell>{err.line || "—"}</TableCell>
									<TableCell>{err.project || "—"}</TableCell>
									<TableCell>{err.issueKey || "—"}</TableCell>
									<TableCell>{err.username || "—"}</TableCell>
									<TableCell>{err.spentOn ? dateFromToString(err.spentOn) : "—"}</TableCell>
									<TableCell align="right">{err.hours ? fmtAmt(err.hours) : "—"}</TableCell>
									<TableCell>{err.reason}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
		</Box>
	);
};

export const RedmineImportPage = () => {
	const manager = useApiManager();
	const ctrl = manager.factory(DedicationRestControllerApiFactory);

	const [attachment, setAttachment] = useState<Attachment | undefined>(undefined);
	const [result, setResult] = useState<DedicationImportResultDto | undefined>(undefined);
	const [error, setError] = useState<string | undefined>(undefined);
	const [running, setRunning] = useState<boolean>(false);

	interface DedicationImportExtDto extends Identifiable {
		attachment?: Attachment;
	}

	/**
	 * Traduce el fallo de Axios a algo accionable. El caso sin respuesta es el importante:
	 * la importación tarda minutos y el servidor puede seguir trabajando aunque se corte la
	 * conexión, así que reintentar a ciegas es justo lo que no hay que hacer.
	 */
	const describeError = (err: any): string => {
		if (!err || !err.response) {
			return "No ha llegado respuesta del servidor. La importación puede seguir en marcha: "
				+ "espera unos minutos y comprueba en Redmine antes de volver a lanzarla.";
		}
		const {status, data} = err.response;
		if (status === 400 && Array.isArray(data)) {
			manageErrors(data as FormError[], form.formId);
			return "Revisa los avisos del formulario.";
		}
		if (data && data.error) {
			return data.error;
		}
		return "El servidor ha respondido con un error " + status + ".";
	};

	const runImport = (): Promise<void> => {
		if (!attachment) {
			return Promise.resolve();
		}
		removePreviousErrors();
		setError(undefined);
		setResult(undefined);
		setRunning(true);

		// silent: el detalle se pinta en el panel, no hace falta el snackbar genérico de error.
		return manager
		.executeRaw(ctrl.importFileDedication({attachment}), undefined, true)
		.then((resp) => {
			const data = resp.data;
			setResult(data);
			manager.enqueueSnackbar(summaryOf(data), {variant: severityOf(data)});
		})
		.catch((err) => setError(describeError(err)))
		.finally(() => setRunning(false));
	};

	const form = useForm({
		factory: () => ({} as DedicationImportExtDto),
		// El guardado real lo lleva la acción de abajo; useForm sólo aporta el contexto del FormFile.
		updater: (bean) => bean,
		defaultAction: {
			label: running ? "Importando…" : "Importar",
			icon: <CloudUpload/>,
			disabled: () => !attachment || running,
			action: () => runImport()
		}
	} as UseFormProps<DedicationImportExtDto>);

	return (
		<FormContent form={form} title={"Importar dedicaciones de Jira"} hideTopButtons hideMetadata>

			<FormFile
				field={"attachment"}
				label={"Archivo CSV"}
				hideShow
				onChange={(attach) => setAttachment(attach)}
				helperText={"Export de Jira con las columnas Project, Key, Title, Started y Time Spent (Hours). "
					+ "Username identifica a la persona en Redmine."}
			/>

			<Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
				Puedes relanzar el fichero sin miedo: un apunte se reconoce por persona, día y tarea, así
				que ni se duplica ni se pierde. Si el fichero trae otras horas para un día que ya está
				importado, se ponen al día —salvo que ese mes ya esté facturado, y entonces se avisa y no
				se toca—. Antes de empezar, asegúrate de{" "}
				<a target={"_blank"} href={"https://redmine.artecoapps.com/settings?tab=notifications"}>
					desactivar las notificaciones
				</a> de correo para no inundar el email.
			</Typography>

			{running && (
				<Alert severity="info" sx={{mt: 3}}>
					<AlertTitle>Importando…</AlertTitle>
					Cada línea son varias llamadas a Redmine, así que un mes entero puede tardar
					varios minutos. No cierres esta pestaña ni vuelvas a lanzar la importación.
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{mt: 3}}>
					<AlertTitle>No se ha podido importar</AlertTitle>
					{error}
				</Alert>
			)}

			{result && <ImportResult res={result}/>}

		</FormContent>
	);
};
